import { BadgePercent, Clock, MapIcon, WandSparkles } from 'lucide-react';
import { Granularity } from 'bindings/time/Granularity';
import CloseIcon from 'images/icons/orders/close.svg';
import CompanyMapBlackImage from 'images/menu_icon/company_map_black.svg';
import * as moment from 'moment';
import * as React from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { getTranslate, Translate } from 'react-localize-redux';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { CheckInCalendar } from '../../../typings/bindings/check_ins/CheckInCalendar';
import { AEventTypes } from '../../atoms/global/events';
import { PanelContext } from '../../components_v2/panel/context';
import { momentToNaiveDateTime } from '../../components_v2/utils';
import storeLang from '../../helpers/storeLang';
import { DefaultButton } from '../../styles/global/css/GlobalButton';
import { TitleFontFamily } from '../../styles/global/css/GlobalText';
import { PrimaryColor, BorderColor2, OrangeSidely, PastelPrimaryColor, RedSidely, SidelyBlack, SurfaceColor } from '../../styles/global/css/Utils';
import { translateToNode, translateToString } from '../../styles/global/translate';
import { useFunctionState } from '../../utils/customHooks';
import { AlertRes } from '../alert/AlertProvider';
import useAlert from '../alert/UseAlert';
import { updateEvent } from '../client-companies/popup/action';
import { checkOpeningHours, openingHoursConflictMessage, worstOpeningHoursConflict } from './openingHours';
import PermissionContext from '../permissions/PermissionContext';
import Restricted from '../permissions/Restricted';
import { Dot } from '../../styles/global/css/Dot';
import { FlexDiv } from '../products/style';
import { getLanguageCode } from '../reports/utils';
import { HoveredEvent } from './CalendarView';
import { getCalendarCheckIns, getCalendarForms, getCalendarFreeForms, getCalendarOrders, getCalendarPromotions, getCalendarShelfAudits, getEvents } from './EventsApi';
import { colorStatusMapping, colorTypeMapping } from './model/Model';

type loadingState = 'loading' | 'loaded' | 'error' | null

export enum EventsTypes {
	Event,
	ShelfAudit,
	FreeForm,
	Order,
	TemporaryEvent,
	CheckIn,
	Promotion,
	Form
}

export class EventsTypesUtils {
	public static toTranslateString(type: EventsTypes): string {
		switch (type) {
			case EventsTypes.Event: return 'event';
			case EventsTypes.ShelfAudit: return 'tool_bar.shelf_audits';
			case EventsTypes.FreeForm: return 'tool_bar.free_forms';
			case EventsTypes.TemporaryEvent: return '';
			case EventsTypes.Order: return 'orders_';
			case EventsTypes.CheckIn: return 'check_in';
			case EventsTypes.Promotion: return 'campagne';
			case EventsTypes.Form: return 'forms';
		}
	}
}


export interface CalendarEvent {
	// visit dragged onto the calendar by hand after a solve: on replay it is
	// mandatory but freely re-orderable, unlike kept planned visits
	draggedIn?: boolean
	type: EventsTypes.Event | EventsTypes.TemporaryEvent
	id: number | string
	eventStatusId: number
	eventTypeId: number
	title: string
	start: Date
	end: Date
	allDay: boolean
	companieName?: string
	incharge: number
	checked?: boolean
	description?: string
	clientCompanyId?: number
	contactId?: number
	contactLastName?: string
	contactFirstName?: string
	created_by?: number
	created_at?: Date
	latitude?: number
	longitude?: number
	form?: number
	scheduled?: boolean
	temporaryModified?: boolean
}

interface ShelfAuditEvents {
	type: EventsTypes.ShelfAudit
	id: number
	start: Date
	end: Date
	title: string
	link?: string
	incharge?: number
	companieName?: string,
	companyId?: number
}

interface FreeForm {
	type: EventsTypes.FreeForm
	clientCompanyId: number
	companyName: string
	description: string
	end: Date
	start: Date
	eventStatusId: number
	incharge: number
	title: string
	id: string
	link: string
}

export interface OrderEvent {
	type: EventsTypes.Order
	id: number
	owner_id: number
	start: Date
	end: Date
	client_company_id: number
	incharge: number
	client_company_name: string
}

export type CheckInEvent = {
	type: EventsTypes.CheckIn
	id: number
	checkIn: CheckInCalendar
	start: Date
	end: Date
}

export type PromotionEvent = {
	type: EventsTypes.Promotion
	promotion_id: number
	start: Date
	end: Date
	color?: string,
	name: string,
	promotion_name: string
}

export type FormEvent = {
	type: EventsTypes.Form
	start: Date
	end: Date
	// unique per block: uuid, or uuid#label when the visit is chunked
	id: string
	// the form instance every chunk opens
	uuid: string
	// display name of the chunk; '' on the main block
	label: string
	// progression < 100, present only when drafts are requested
	draft: boolean
	// hex color configured on the chunk's start field, if any
	color?: string | null
	form_id: number
	form_name: string
	owner_id: number,
	name: string
}

export type CalendarEvents = CalendarEvent | ShelfAuditEvents | FreeForm | OrderEvent | CheckInEvent | PromotionEvent | FormEvent

const localizer = momentLocalizer(moment);

const DragAndDropCalendar = withDragAndDrop(Calendar);

const DandDCalendar = styled(DragAndDropCalendar)`
	.rbc-allday-cell {
		overflow: hidden;
	}
	.rbc-header {
		text-align: unset;
	}
	.rbc-header > button, .rbc-show-more  {
		border: none;
		background-color: transparent;
		width: 100%;
		text-align: left;
		font-weight: 500;
	}
	.rbc-show-more {
		color: ${PrimaryColor};
		cursor: pointer;
		font-size: 12px;
	}
	.rbc-row-content {
		overflow: auto;
		height: 100%;
	}
	.rbc-today {
		background-color: ${PastelPrimaryColor};
	}
`;


const CalendarPanelContainer = styled(FlexDiv)`
	border: 1px solid ${BorderColor2};
	border-radius: 5px;
`;

const CalendarPanel = styled(DefaultButton) <{ selected?: boolean }>`
	color: ${SidelyBlack}${p => p.selected ? '' : '40'};
	${p => p.selected ? 'font-weight: 500;' : ''}
	border-radius: 4px;
	background ${p => p.selected ? `${BorderColor2}80` : 'transparent'};
	margin: 0;
	height: 24px;
	width: fit-content;
	transition: unset;
	line-height: 0px;
	padding: 0 10px;
	width: 75px;

	&:hover {
		filter: unset !important;
	}
`;

// Week number shown before the toolbar label ("S31" fr / "W31" en / "KW31" de).
const WeekNumberTag = styled.span`
	font-size: 15px;
	font-weight: 500;
	opacity: 0.55;
	flex-shrink: 0;
`;

const PeriodLabel = styled.span`
	font-family: ${TitleFontFamily};
	font-size: 15px;
`;

const CalendarToolbarButton = styled(DefaultButton) <{ position?: 'first' | 'last', selected?: boolean }>`
	margin: 0;
	border-radius: 0;
	height: 24px;
	color: ${SidelyBlack};
	border: 1px solid ${BorderColor2};
	color: ${SidelyBlack};
	margin-left: -1px;
	${p => p.position === 'first' ? 'border-radius: 5px 0 0 5px;' : p.position === 'last' ? 'border-radius: 0 5px 5px 0;' : ''}
	&:hover {
		background: ${BorderColor2}80;
		filter: none !important;
	}
	background: ${p => p.selected ? `${BorderColor2}80` : 'transparent'};
	font-size: 11px;
	width: fit-content;
	line-height: 20px;
	padding: 0 5px;
	min-width: 24px;
`;

const ClockIconContainer = styled.div`
	position: absolute;
	top: 0px;
	right: 0px;
	filter: opacity(50%);
`;

const RemoveTemporaryEventButton = styled.button`
	position: absolute;
	top: 1px;
	right: 2px;
	width: 12px;
	height: 12px;
	padding: 0;
	border: none;
	border-radius: 50%;
	background: color-mix(in oklab, ${SidelyBlack} 35%, transparent);
	color: white;
	font-size: 10px;
	line-height: 1;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 2;
	opacity: 0;
	transition: opacity 0.1s;
	pointer-events: none;
	.rbc-event:hover &,
	&:focus,
	&:focus-within {
		opacity: 1;
		pointer-events: auto;
	}
	&:hover {
		background: color-mix(in oklab, ${RedSidely} 80%, transparent);
	}
`;

const ReplayDayButton = styled.button`
	border: none;
	background: transparent;
	color: ${SidelyBlack};
	padding: 0;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	line-height: 0;
	&:hover {
		opacity: 0.7;
	}
`;

function capitalizeFirstLetter(string: string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

function getStyleFormType(className: string, color: string, type: EventsTypes, restricted: boolean | undefined, event_id: number | string, hoveredEvent: HoveredEvent, scheduled: boolean) {
	const filter = hoveredEvent && event_id != hoveredEvent.id && !hoveredEvent.fromCalendar ? 'brightness(50%)' : '';
	switch (type) {
		case EventsTypes.ShelfAudit:
		case EventsTypes.FreeForm:
		case EventsTypes.Order:
		case EventsTypes.CheckIn:
		case EventsTypes.Form:
			return {
				className,
				backgroundColor: SidelyBlack,
				border: 'none',
				color: 'white',
				outline: `4px solid ${color}`,
				filter
			};
	}
	const isTemporary = type !== EventsTypes.TemporaryEvent && restricted;
	return {
		className,
		backgroundColor: isTemporary ? SurfaceColor : color,
		border: scheduled ? `3px solid color-mix(in oklab, ${SidelyBlack} 20%, transparent)` : (isTemporary ? `2px solid ${color}` : undefined),
		color: isTemporary ? SidelyBlack : 'white',
		filter: scheduled ? 'brightness(1.1)' : filter,
		fontSize: '12px',
	};
}

export function getEventStatusColor(status: number): string | undefined {
	return colorStatusMapping[status - 1].color;
}

// add dot before event name from status
function getDot(status?: number) {
	if (!status) return <></>;
	const color = getEventStatusColor(status);
	if (!color) return <></>;
	return (<span className="dot" style={{
		backgroundColor: color,
		width: '15px',
		height: '15px',
		minWidth: '15px',
		boxShadow: `0px 0px 3px color-mix(in oklab, ${SidelyBlack} 33%, transparent)`,
		flexShrink: 0
	}} />);
}

const formats = {
	eventTimeRangeFormat: ({ start, end }) => {
		if (end.getTime() - start.getTime() < 3600000) { // 1h
			return '';
		}
		return `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`;
	}
};

function hashRequest(lst: unknown[]) {
	let ret = '';
	for (const item of lst) {
		ret = ret + JSON.stringify(item);
	}
	return ret;
}

function toCalendarFreeForms(freeForms) {
	const ret: CalendarEvents[] = freeForms.map(currFF => {
		const start = new Date(Date.parse(currFF.date));
		const end = new Date(start.getTime() + 1000 * 60 * 45); // add 60mins
		return (
			{
				type: EventsTypes.FreeForm,
				id: currFF.id,
				start,
				end,
				// companieName: currSA.company_name ? currSA.company_name : "",
				title: currFF.title,
				link: currFF.link,
				incharge: currFF.incharge,
			});
	});
	return ret;
}

// turn fetched events into calendarShelfAudit
function toCalendarShelfAudits(shelfAudits): CalendarEvents[] {
	const translate = getTranslate(storeLang.getState().localize);
	const ret: CalendarEvents[] = shelfAudits.map(currSA => {
		const start = new Date(Date.parse(currSA.date));
		const end = new Date(start.getTime() + 1000 * 60 * 45); // add 60mins

		return (
			{
				type: EventsTypes.ShelfAudit,
				id: currSA.id,
				start,
				end,
				companieName: currSA.company_name ? currSA.company_name : undefined,
				companyId: currSA.company_id,
				title: translate('calendar.shelf_audits'),
				incharge: currSA.incharge,
			});
	});
	return ret;
}

export function toTemporaryEvents(events): CalendarEvents[] {
	const ret: CalendarEvents[] = events.map((currEv, i) => {
		return (
			{
				...currEv,
				type: EventsTypes.TemporaryEvent,
				end: currEv.endDate,
				start: currEv.startDate,
				id: currEv.id !== undefined ? currEv.id : `temporaryEvent[${i}]`
			});
	});
	return ret;
}

// get upper and lower bounds of api call
function dateRangeToFecth(obj, key?: string) {
	let avg = 0;
	if (obj.start) {
		avg = (obj.start.getTime() + obj.end.getTime()) / 2;
	} else {
		const length = obj.length;
		avg = obj.map(e => e.getTime())
			.reduce((a, b) => a + b, 0);
		avg /= length;
	}
	if (!key) {
		if (Array.isArray(obj)) {
			key = 'week';
		} else if (obj instanceof Date) {
			key = 'day';
		} else {
			key = 'month';
		}
	}
	const rawStart = new Date(avg);
	const rawEnd = new Date(avg);
	let start;
	let end;
	switch (key) {
		case 'month':
			rawStart.setMonth(rawStart.getMonth() - 2);
			start = new Date(rawStart.getFullYear(), rawStart.getMonth());
			rawEnd.setMonth(rawEnd.getMonth() + 3);
			end = new Date(rawEnd.getFullYear(), rawEnd.getMonth());
			break;
		case 'week':
			rawStart.setDate(rawStart.getDate() - 10);
			rawStart.setHours(0, 0, 0, 0);
			start = rawStart;
			rawEnd.setDate(rawEnd.getDate() + 11);
			rawEnd.setHours(0, 0, 0, 0);
			end = rawEnd;
			break;
		case 'day':
			rawStart.setDate(rawStart.getDate() - 2);
			rawStart.setHours(0, 0, 0, 0);
			start = rawStart;
			rawEnd.setDate(rawEnd.getDate() + 3);
			rawEnd.setHours(0, 0, 0, 0);
			end = rawEnd;
			break;
	}
	return { start, end };
}

export type CalendarPeriod = 'week' | 'day' | 'month';

export type EventsBoundsForMapView = {
	date: moment.Moment;
	granularity: Granularity;
};

export default function SidelyCalendar(props: {
	setEvents: (lst: CalendarEvents[]) => void
	events: CalendarEvents[]
	// form ids / chunk labels hidden by the calendar's form filters
	hiddenForms?: number[]
	hiddenBounds?: string[]
	onSelectSlot?: (e) => void
	onSelectEvent?: (e: CalendarEvents) => void
	setLoadingState: (loadingState: loadingState) => void
	loadingState: loadingState
	filters?
	toolBarState?
	reset?: boolean
	setReset?: (b: boolean) => void
	style
	restricted?: boolean
	id?: number
	defaultDate?: Date
	onEventHover?: (event?: CalendarEvents) => void
	defaultView?: CalendarPeriod
	hoveredEvent?: HoveredEvent
	eventStyle?: (event: CalendarEvents, hovered: boolean) => ({ style })
	onMapClicked?: React.Dispatch<React.SetStateAction<EventsBoundsForMapView>>
	date?: Date
	onDateChange?: (d: Date) => void
	/** Fired when the user switches between day, week and month. */
	onViewChange?: (view: CalendarPeriod) => void
	/** Colour of the dot drawn before a day's header, or nothing for no dot.
	 * `hasMapEntries` tells whether that day holds events the map can show. */
	dayDotColor?: (date: Date, hasMapEntries: boolean) => string | undefined
	/** Makes the dot clickable: the quick filter that hides or shows that trip. */
	onDayDotClick?: (date: Date) => void
	isMapOpen?: boolean
	column?: boolean,
	mapGranularity?: Granularity
	/** Optional: render a remove (X) affordance on each TemporaryEvent. */
	onRemoveTemporaryEvent?: (event: CalendarEvents) => void
	/** Optional: render a "Replay" button in the day header when any
	 * TemporaryEvent lives on that day. Called with the JS Date of the day. */
	onReplayDay?: (date: Date) => void
	/** Optional: external-drop callback (react-big-calendar's onDropFromOutside).
	 * Fired when the user drops something dragged from outside the calendar
	 * (e.g. a map pin) onto a slot. `start`/`end` are the slot the user dropped on. */
	onDropFromOutside?: (args: { start: Date | string, end: Date | string, allDay: boolean }) => void
	/** Optional: rbc calls this during drag-over to know the shape of the
	 * incoming item (drives the highlighted slot's duration). Pair with onDropFromOutside.
	 * Must include `type` / `eventTypeId` so the event style getter can find a colour;
	 * otherwise the calendar will crash when the user drags an item over a slot. */
	dragFromOutsideItem?: () => { start: Date | string, end: Date | string, [key: string]: unknown }
}) {
	const { onMapClicked, column, mapGranularity, onRemoveTemporaryEvent, onReplayDay, onDropFromOutside, dragFromOutsideItem, dayDotColor, onDayDotClick } = props;
	const translate = getTranslate(storeLang.getState().localize);
	let reloadApi = false;
	const [isApiRunning, setApiRunning] = React.useState<boolean>(false);
	const [lastestRequest, setLastestRequest] = React.useState<string>('');
	const [calendarView, setCalendarView] = useFunctionState<CalendarPeriod>(props.defaultView ?? 'week', undefined, (view) => {
		props.onViewChange?.(view);
		props.onMapClicked?.((currentState) => {
			if (!currentState) return currentState;
			return {
				...currentState,
				granularity: view
			};
		});
	});
	const [displayedDate, setdisplayedDate] = React.useState(dateRangeToFecth([new Date(Date.now())], calendarView));
	const [date, setDate_] = React.useState<Date>(props.defaultDate ?? new Date());
	const [clicked, setClicked] = React.useState<boolean>(false);
	const setDate = (date: Date) => {
		setDate_(date);
		props.onDateChange?.(date);
	};
	const eventTypes = useRecoilValue(AEventTypes);
	const alert = useAlert();
	const { isAllowedTo } = React.useContext(PermissionContext);

	const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
	const [refresh, setRefresh] = React.useState<boolean>(false);

	const scrollToTime = React.useMemo(() => moment().set({ h: Number(localStorage.getItem('startCalendarHour') ?? 6), m: 0 }).toDate(), []);


	React.useEffect(() => {
		const timeout = setTimeout(() => {
			setRefresh(true);
		}, 500);

		return () => clearTimeout(timeout);
	}, [isLoaded]);

	React.useEffect(() => {
		if (props.date) setDate_(props.date);
	}, [props.date]);

	React.useEffect(() => {
		if (props.reset) {
			props.setReset?.(false);
			setLastestRequest('');
			props.setLoadingState('loading');
		}
	}, [props.reset]);

	// fetch data
	React.useEffect(() => {
		if (props.toolBarState && props.toolBarState.userId === undefined) {
			return;
		}
		const request = hashRequest([props.filters, props.toolBarState, displayedDate]);
		if (lastestRequest && request == lastestRequest) {
			reloadApi = false;
		} else if (isApiRunning) {
			reloadApi = true;
		} else {
			reloadApi = false;
			setApiRunning(true);
			setLastestRequest(request);
			const apis = async() => {
				try {
					props.setLoadingState('loading');
					let list: CalendarEvents[] = [];
					if (props.filters) {
						for (const type of props.filters.types) {
							switch (type) {
								case EventsTypes.Event: {
									const events = await getEvents(displayedDate, props.toolBarState);
									list = list.concat(events);
									break;
								}
								case EventsTypes.ShelfAudit: {
									const shelf_audits = await getCalendarShelfAudits(displayedDate, props.toolBarState);
									const calendarShelfAudits = toCalendarShelfAudits(shelf_audits);
									list = list.concat(calendarShelfAudits);
									break;
								}
								case EventsTypes.FreeForm: {
									const free_forms = await getCalendarFreeForms(displayedDate, props.toolBarState);
									const calendarFreeForms = toCalendarFreeForms(free_forms);
									list = list.concat(calendarFreeForms);
									break;
								}
								case EventsTypes.Order: {
									const orders = await getCalendarOrders(displayedDate, props.toolBarState);
									list = list.concat(orders);
									break;
								}
								case EventsTypes.CheckIn: {
									const checkIns = await getCalendarCheckIns(displayedDate, props.toolBarState);
									list = list.concat(checkIns);
									break;

								}
								case EventsTypes.Promotion: {
									const promotions = await getCalendarPromotions(displayedDate);
									list = list.concat(promotions);
									break;
								}
								case EventsTypes.Form: {
									const forms = await getCalendarForms(displayedDate, props.toolBarState, props.filters?.includeDrafts);
									list = list.concat(forms);
									break;
								}
							}
						}
					} else {
						const events = await getEvents(displayedDate, props.toolBarState ?? { userId: props.id });
						list = list.concat(events);
					}
					setApiRunning(false);
					list = list.concat(props.events.filter(e => e.type == EventsTypes.TemporaryEvent));
					props.setEvents(list);
					if (reloadApi) {
						props.setLoadingState('loading');
					} else {
						props.setLoadingState('loaded');
						setIsLoaded(true);
					}
				} catch (e) {
					console.error(e);
					setApiRunning(false);
					props.setLoadingState('error');
				}
			};
			apis();
		}
	}, [props.loadingState, displayedDate, props.toolBarState, props.filters]);

	function customEvent(props: { event: CalendarEvents }) {
		const { event } = props;
		let value: React.ReactNode = '';
		switch (event.type) {
			case EventsTypes.CheckIn:
				value = event.checkIn.company_name ?? 'check in';
				break;
			case EventsTypes.Promotion:
				value = <>
					<div style={{ flexShrink: 0 }}><BadgePercent size={16} /></div>
					{event.promotion_name} - {event.name}
				</>;
				break;
			default:
				if ('companieName' in event) value = event.companieName ?? event.title;
				else if ('title' in event) value = event.title;
				else if ('name' in event) value = event.name;
		}
		const isTemporary = event.type === EventsTypes.TemporaryEvent;
		return (
			<>
				<FlexDiv style={{ whiteSpace: 'nowrap' }} gap='0.5em' margin='0 0 0 10px'>
					{'eventStatusId' in event && getDot(event.eventStatusId)}
					<FlexDiv gap='0.5em'>
						{value}
					</FlexDiv>
				</FlexDiv>
				{'scheduled' in event && event?.scheduled && <ClockIconContainer><Clock size={15} /></ClockIconContainer>}
				{isTemporary && onRemoveTemporaryEvent && (
					<RemoveTemporaryEventButton
						title={translateToString('remove_proposal') || 'Remove proposal'}
						onClick={(e) => {
							e.stopPropagation();
							onRemoveTemporaryEvent(event);
						}}
					>
						×
					</RemoveTemporaryEventButton>
				)}
			</>
		);
	}

	// static traductions for Big Calendar
	const localTraductions = {
		next: translate('next'),
		previous: translate('previous'),
		today: translate('today'),
		month: translate('month'),
		week: translate('Week'),
		day: translate('day'),
		showMore: (count: number) => translateToString('calendar_show_more', [['VALUE', count.toString()]])
	};

	async function onEventDrop(event) {
		switch (event.event.type as EventsTypes) {
			case EventsTypes.ShelfAudit:
			case EventsTypes.FreeForm:
			case EventsTypes.Order:
			case EventsTypes.CheckIn:
			case EventsTypes.Promotion:
			case EventsTypes.Form:
				return;
		}
		if (event.isAllDay) {
			event.start.setHours(0, 0, 0);
			event.end.setHours(23, 59, 0);
			event.event.allDay = true;
		}
		const newEvent = {
			...event.event,
			start: event.start,
			end: event.end
		};
		if (event.event.scheduled) {
			toast.error(translateToString('company.creation.event.moving_scheduled'));
			return;
		}
		if (!props.restricted) {
			if (event.event.type == EventsTypes.Event) {
				if (!isAllowedTo({ objectAction: 'UpdateEvent' }, { ownerId: newEvent.incharge })) return;
				// the opening-hours check of the dropped slot: a block keeps the
				// event where it was, a warn tells and moves it
				if (event.event.clientCompanyId && !event.isAllDay) {
					const conflicts = await checkOpeningHours({
						client_company_id: event.event.clientCompanyId,
						event_type_id: event.event.eventTypeId,
						start_date: momentToNaiveDateTime(moment(event.start).utc()),
						end_date: momentToNaiveDateTime(moment(event.end).utc()),
						utc_offset_minutes: moment(event.start).utcOffset(),
					}).catch(() => []);
					const conflict = worstOpeningHoursConflict(conflicts);
					if (conflict) {
						const blocking = conflict.mode === 'block';
						await alert({
							title: translateToNode(blocking ? 'opening_hours_rule.blocked_title' : 'opening_hours_rule.warning_title'),
							svg: blocking ? 'error' : 'warning',
							content: <div style={{ color: blocking ? RedSidely : OrangeSidely }}>{openingHoursConflictMessage(conflict)}</div>,
							buttons: [{ title: 'Ok', res: AlertRes.Ok }],
						});
						if (blocking) return;
					}
				}
				updateEvent(event.event.id, { start: momentToNaiveDateTime(moment(event.start).utc()), end: momentToNaiveDateTime(moment(event.end).utc()), utc_offset_minutes: moment(event.start).utcOffset() })
					.catch(() => {
						alert({
							title: translateToNode('error'),
							content: translateToNode('error_while_modifying_event'),
							buttons: [{
								title: 'Ok',
								res: AlertRes.Ok
							}]
						});
						const index = props.events.findIndex(e => 'id' in e && e.id === event.event.id);
						if (index >= 0) {
							props.events[index] = event.event;
						}
						props.setEvents([...props.events]);
					});
			}
		}
		if (newEvent.type == EventsTypes.Event) {
			if (!isAllowedTo({ objectAction: 'UpdateEvent' }, { ownerId: newEvent.incharge })) return;
			newEvent.temporaryModified = true;
		}
		const index = props.events.findIndex(e => 'id' in e && e.id === newEvent.id);
		if (index >= 0) {
			props.events[index] = newEvent;
		}
		props.setEvents([...props.events]);
	}

	// change events style by type
	const getEventTypeStyle = (event: CalendarEvents) => {
		if (props.eventStyle != null) {
			return props.eventStyle(event, 'id' in event && event.id == props.hoveredEvent?.id);
		} else if (event.type === EventsTypes.Promotion) {
			const filter = props.hoveredEvent && !props.hoveredEvent.fromCalendar ? 'brightness(50%)' : '';
			return {
				style: {
					backgroundColor: SurfaceColor,
					border: `1px solid ${event.color ?? PrimaryColor}`,
					padding: 0,
					color: SidelyBlack,
					filter
				}
			};
		} else {
			let switchOn: number;
			if ('eventTypeId' in event) {
				switchOn = event.eventTypeId;
			} else {
				switchOn = event.type;
			}
			const scheduled = !!('scheduled' in event && event?.scheduled);
			if (!Number.isFinite(switchOn) || switchOn < 1 || switchOn > 20) switchOn = 9;
			if (event.type === EventsTypes.Form) {
				// one consistent look for every form block: black body, colored
				// ring. The ring is a border (drawn inside the box, always fully
				// visible) instead of the outline, which sits outside the box
				// and disappears under any adjacent block. A chunk's start field
				// can swap the ring color; an unfinished instance washes out.
				const ring = event.color ?? colorTypeMapping[switchOn - 1].color;
				const filter = props.hoveredEvent && event.id != props.hoveredEvent.id && !props.hoveredEvent.fromCalendar ? 'brightness(50%)' : '';
				return { style: { backgroundColor: SidelyBlack, border: `3px solid ${ring}`, color: 'white', filter, ...(event.draft ? { opacity: 0.55 } : {}) } };
			}
			return { style: getStyleFormType('', colorTypeMapping[switchOn - 1].color, event.type, props.restricted, event.id, props.hoveredEvent, scheduled) };
		}
	};

	// eslint-disable-next-line react/display-name
	const dateHeader = (week: boolean, events: CalendarEvents[], isMapOpen = false) => (props: { date: Date, label: string, onDrillDown: () => void }): React.ReactNode => {
		const start = moment(props.date).startOf('day');
		const end = moment(props.date).endOf('day');
		// The magic-wand replay only appears once the user dragged a new company
		// onto this day: it is the "fit my additions into the route" action, not
		// a general re-solve.
		const dayHasDraggedIn = events.some(e =>
			e.type === EventsTypes.TemporaryEvent
			&& (e as CalendarEvent).draggedIn
			&& e.end.getTime() >= start.valueOf()
			&& e.start.getTime() <= end.valueOf()
		);
		const showReplay = week && onReplayDay !== undefined && dayHasDraggedIn;
		const hasMapEntries = onMapClicked !== undefined && events.some(e =>
			e.end.getTime() >= start.valueOf()
			&& e.start.getTime() <= end.valueOf()
			&& ('checkIn' in e || (e.type === EventsTypes.Event && e.latitude && e.longitude && eventTypes.some(et => et.id === e.eventTypeId && et.map)))
		);
		const dotColor = dayDotColor?.(props.date, hasMapEntries);
		const label = <FlexDiv gap='6px' align='center' justify={week ? undefined : 'flex-end'} style={{ cursor: 'pointer' }} onClick={props.onDrillDown}>
			{dotColor && <Dot
				color={dotColor}
				size='10px'
				cursor={onDayDotClick ? 'pointer' : undefined}
				onClick={onDayDotClick ? e => {
					e.stopPropagation();
					onDayDotClick(props.date);
				} : undefined}
			/>}
			<div>{props.label}</div>
		</FlexDiv>;
		if (!hasMapEntries && !showReplay) return label;
		const flexProps = week ? { gap: '10px' } : { margin: '0 15px 0 0' };
		return (
			<FlexDiv justify='space-between' {...flexProps}>
				{label}
				<FlexDiv gap='6px' align='center'>
					{showReplay && (
						<ReplayDayButton
							title={translateToString('replay_day_button') || 'Replay'}
							onClick={(e) => {
								e.stopPropagation();
								onReplayDay!(props.date);
							}}
						>
							<WandSparkles size={16} />
						</ReplayDayButton>
					)}
					{hasMapEntries && onMapClicked && (
						<Restricted to={{ objectAction: 'ViewMap' }}>
							<div style={{ cursor: 'pointer', marginTop: '-5px', opacity: mapGranularity === 'day' && props.date.toDateString() === date.toDateString() && isMapOpen ? undefined : 0.3 }} onClick={_ => {
								setClicked(true);
								onMapClicked({ date: start, granularity: 'day' });
							}}>
								<MapIcon />
							</div>
						</Restricted>
					)}
				</FlexDiv>
			</FlexDiv>
		);
	};

	if (!props.loadingState) {
		return (<></>);
	}

	// eslint-disable-next-line react/display-name
	const ToolBar = (events: CalendarEvents[], isMapOpen = false) => (props: {
		date: Date,
		label: string,
		localizer,
		onNavigate: (action, newDate: Date) => void,
		onView: (view: CalendarPeriod) => void,
		view: CalendarPeriod,
		views: Array<CalendarPeriod>
	}): JSX.Element => {
		const startOfPeriod = moment(props.date).startOf(props.view);
		const endOfPeriod = moment(props.date).endOf(props.view);
		const map = onMapClicked && events.some(e => e.start.getTime() >= startOfPeriod.valueOf() && e.start.getTime() <= endOfPeriod.valueOf() && ('checkIn' in e || (e.type === EventsTypes.Event && e.latitude && e.longitude && eventTypes.some(evenType => evenType.id === e.eventTypeId && evenType.map))));
		const { setIsPanelOpen } = React.useContext(PanelContext);

		const onNavigate = (period: PeriodNavigatorType) => {
			switch (period) {
				case 'previous': return props.onNavigate('previous', moment(props.date).subtract(1, props.view).toDate());
				case 'today': return props.onNavigate('today', moment().toDate());
				case 'next': return props.onNavigate('next', moment(props.date).add(1, props.view).toDate());
			}
		};

		if (column) {
			return <FlexDiv width='100%' padding='10px 0px' fontWeight='700' color={SidelyBlack} gap='5px' flow='column' align='flex-start'>
				<FlexDiv justify='space-between' width='100%'>
					<div>
						{props.view !== 'month' && <WeekNumberTag style={{ marginRight: '5px' }}>{translateToString('week_short')}{moment(props.date).week()}</WeekNumberTag>}
						<PeriodLabel>{capitalizeFirstLetter(props.label)}</PeriodLabel>
						<Restricted to={{ objectAction: 'ViewMap' }}>
							{map && onMapClicked && <a onClick={_ => {
								setClicked(true);
								onMapClicked({ date: startOfPeriod, granularity: props.view });
							}} style={{ marginLeft: '5px' }}>
								<img src={CompanyMapBlackImage} style={{ height: 21, marginTop: '-5px', opacity: mapGranularity === props.view && props.date.toDateString() === date.toDateString() && isMapOpen ? undefined : 0.3 }} />
							</a>}
						</Restricted>
					</div>
					<ToolBarCloseButton onClick={() => setIsPanelOpen(false)}>
						<img src={CloseIcon} width={24} />
					</ToolBarCloseButton>
				</FlexDiv>
				<FlexDiv width='100%' justify='space-between'>
					<PeriodViewSelector
						{...props}
						selectedView={props.view}
					/>
					<PeriodNavigator onNavigate={onNavigate} />
				</FlexDiv>
			</FlexDiv>;
		}

		return (
			<FlexDiv justify='space-between' width='100%' padding='0 0px 10px 1px' fontWeight='700' color={SidelyBlack} gap='5px'>
				<FlexDiv gap='20px' width='33%'>
					<FlexDiv gap='5px'>
						{props.view !== 'month' && <WeekNumberTag>{translateToString('week_short')}{moment(props.date).week()}</WeekNumberTag>}
						<PeriodLabel>{capitalizeFirstLetter(props.label)}</PeriodLabel>
					</FlexDiv>
					<Restricted to={{ objectAction: 'ViewMap' }}>
						{map && onMapClicked && <a onClick={_ => {
							setClicked(true);
							onMapClicked({ date: startOfPeriod, granularity: props.view });
						}}>
							<div style={{ opacity: mapGranularity === props.view && props.date.toDateString() === date.toDateString() && isMapOpen ? undefined : 0.3 }}>
								<MapIcon />
							</div>
						</a>}
					</Restricted>
				</FlexDiv>
				<FlexDiv width='33%' justify='center'>
					<PeriodViewSelector
						{...props}
						selectedView={props.view}
					/>
				</FlexDiv>
				<FlexDiv width='33%' justify='right'>
					<PeriodNavigator onNavigate={onNavigate} />
				</FlexDiv>
			</FlexDiv>
		);
	};


	// hidden forms/bounds are filtered at render only: the state keeps every
	// fetched event so the filter panel can still list all options
	const displayedEvents = props.events.filter(e => e.type !== EventsTypes.Form
		|| (!props.hiddenForms?.includes(e.form_id) && !props.hiddenBounds?.includes(e.label)));

	return (
		<DandDCalendar
			key={`CalendarRerenderEvents${JSON.stringify({ events: displayedEvents.map(e => 'id' in e ? e.id : e.promotion_id), time: date.toDateString(), open: props.isMapOpen, refresh: refresh })}`}
			culture={getLanguageCode()}
			messages={localTraductions}
			localizer={localizer}
			events={displayedEvents}
			style={props.style}
			scrollToTime={scrollToTime}
			views={{ day: true, week: true, month: true }}
			eventPropGetter={getEventTypeStyle}
			onSelectEvent={props.onSelectEvent}
			onRangeChange={e => {
				const ret = dateRangeToFecth(e);
				setdisplayedDate(ret);
			}}
			components={{
				month: { dateHeader: dateHeader(false, props.events, props.isMapOpen) },
				week: { header: dateHeader(true, props.events, props.isMapOpen) },
				day: { header: dateHeader(true, props.events, props.isMapOpen) },
				toolbar: ToolBar(props.events, props.isMapOpen),
				event: customEvent,
				eventWrapper: ({ event, children }) => (
					<div
						onMouseEnter={() => props.onEventHover?.(event)}
						onMouseLeave={() => props.onEventHover?.()}
					>
						{children}
					</div>
				)
			}}
			date={date}
			onNavigate={setDate}
			startAccessor="start"
			endAccessor="end"
			onEventDrop={onEventDrop}
			selectable={props.onSelectSlot && isAllowedTo({ objectAction: 'CreateEvent' })}
			onSelectSlot={e => {
				if (clicked) {
					setClicked(false);
				} else {
					props.onSelectSlot?.(e);
				}
			}}
			view={calendarView}
			onView={setCalendarView}
			formats={formats}
			defaultDate={props.defaultDate}
			onEventResize={onEventDrop}
			resizable
			resizableAccessor={(event) => event.type === EventsTypes.Event}
			draggableAccessor={(event) => event.type === EventsTypes.Event}
			dateRange
			allDayMaxRows={3}
			popup
			onDropFromOutside={onDropFromOutside}
			dragFromOutsideItem={dragFromOutsideItem}
		/>
	);
}

export type PeriodView = 'day' | 'week' | 'month' | 'quarter' | 'year'

export function PeriodViewSelector(props: { views: PeriodView[], onView: (v: PeriodView) => void, selectedView?: PeriodView }) {
	return <CalendarPanelContainer>
		{props.views.map(v =>
			<CalendarPanel
				key={`PeriodViewSelectorButton${v}`}
				onClick={() => props.onView(v)}
				selected={v === props.selectedView}
			>
				<Translate id={v} />
			</CalendarPanel>
		)}
	</CalendarPanelContainer>;
}
export type PeriodNavigatorType = 'previous' | 'today' | 'next';

export function PeriodNavigator(props: { onNavigate: (navigate: PeriodNavigatorType) => void }) {
	return <FlexDiv>
		<CalendarToolbarButton onClick={() => props.onNavigate('previous')} position='first' >{'<'}</CalendarToolbarButton>
		<CalendarToolbarButton onClick={() => props.onNavigate('today')}><Translate id='today' /></CalendarToolbarButton>
		<CalendarToolbarButton onClick={() => props.onNavigate('next')} position='last' >{'>'}</CalendarToolbarButton>
	</FlexDiv>;
}


const ToolBarCloseButton = styled.button`
	background: none;
	border: none;
	padding: 0;
	width: 24px;
	height: 24px;
	display: flex;
`;
